import { getAccessToken } from '../authentication/supabaseService';
import { getDemoAccountUserIds } from './superAdminSettingsService';

const API_BASE = '/api/admin';

/** Hidden per-user classification, super-admin only — must stay identical to the server's
 *  `INTERNAL_ROLES` in `server/routes/superAdmin/rolesRoutes.ts`. Never shown to customers. */
export const INTERNAL_ROLES = ['culinary_cloud_user', 'app_only_user'] as const;
export type InternalRole = (typeof INTERNAL_ROLES)[number];

export interface CommunicationStaff {
  id: string;
  email: string;
  name: string | null;
  restaurantRole: string | null;
  internalRole: string | null;
  restaurantNames: string[];
}

/** Admin (owner) accounts carry no restaurant role — they are implicitly "Owner". */
export interface CommunicationAdmin {
  id: string;
  email: string;
  name: string | null;
  internalRole: string | null;
  staff: CommunicationStaff[];
}

export interface RolesOverview {
  summary: {
    totalStaff: number;
    staffWithRole: number;
  };
  admins: CommunicationAdmin[];
}

async function authHeaders() {
  const token = await getAccessToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/** Demo accounts are excluded from the whole roles payload — same mechanism the admin
 *  overview uses (`excludeUserIds` query param, applied to admins AND their staff). */
async function demoExclusionParam(): Promise<string> {
  const demoUserIds = await getDemoAccountUserIds();
  if (demoUserIds.length === 0) return '';
  return `?excludeUserIds=${encodeURIComponent(JSON.stringify(demoUserIds))}`;
}

/** Every non-demo admin account with its staff, their restaurant role, internal role and
 *  the restaurants they are assigned to. */
export async function getRolesOverview(): Promise<RolesOverview> {
  const excludeParam = await demoExclusionParam();
  const res = await fetch(`${API_BASE}/communication/roles-overview${excludeParam}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch roles overview: ${res.statusText}`);
  }
  return res.json();
}

/** Set a staff member's restaurant role. Same side effect as the in-app staff-card dropdown:
 *  it also rewrites the allowed revenue source types on all their restaurants. */
export async function setStaffRestaurantRole(staffId: string, restaurantRole: string): Promise<void> {
  const res = await fetch(`${API_BASE}/staff/${staffId}/role`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify({ restaurantRole }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update role: ${res.statusText}`);
  }
}

/** Set (or clear, with null) a user's internal role. Works for admins and staff. */
export async function setUserInternalRole(userId: string, internalRole: InternalRole | null): Promise<void> {
  const res = await fetch(`${API_BASE}/users/${userId}/internal-role`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify({ internalRole }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update internal role: ${res.statusText}`);
  }
}
